import { useParams, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  History,
  ArrowLeft,
  CheckCircle2,
  Clock,
  FileText,
  User,
  Calendar,
  Shield,
  Lock,
  TrendingUp,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";

export default function QuestionarioHistoricoDetalhe() {
  usePageTitle("Detalhes do Histórico");
  const { user } = useAuth();
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const campaignId = parseInt(params.id || "0");

  // V82b: Consultor Canal não tem acesso ao módulo de Questionários
  if (user?.role === "channel_consultant") {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto" />
            <h2 className="text-xl font-semibold text-muted-foreground">Acesso Restrito</h2>
            <p className="text-sm text-muted-foreground">Seu perfil não possui acesso ao módulo de Questionários.</p>
            <Button variant="outline" onClick={() => setLocation("/home/canal")}>Voltar ao Início</Button>
          </div>
        </div>
      </div>
    );
  }

  // Queries
  const { data: campaign, isLoading: loadingCampaign } = trpc.questionnaire.history.getCampaignDetail.useQuery(
    { campaignId },
    { enabled: !!campaignId }
  );

  const { data: finalSnapshot, isLoading: loadingSnapshot } = trpc.questionnaire.monitoring.getFinalSnapshot.useQuery(
    { campaignId },
    { enabled: campaign?.status === "CLOSED" }
  );

  if (loadingCampaign) {
    return (
      <div className="container py-8">
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Carregando detalhes da campanha...
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="container py-8">
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Campanha não encontrada
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusConfig = {
    DRAFT: { label: "Rascunho", variant: "secondary" as const, icon: Clock, color: "text-gray-600" },
    ACTIVE: { label: "Em Andamento", variant: "default" as const, icon: Clock, color: "text-blue-600" },
    CLOSED: { label: "Encerrada (Oficial)", variant: "outline" as const, icon: CheckCircle2, color: "text-green-600" },
  };

  const status = statusConfig[campaign.status];
  const StatusIcon = status.icon;

  return (
    <div className="container py-8 space-y-6">
      {/* Cabeçalho */}
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => setLocation("/questionarios/historico")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar ao Histórico
        </Button>

        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <History className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold">Campanha #{campaign.id}</h1>
            </div>
            <p className="text-muted-foreground">
              Histórico auditável e rastreável da campanha de questionário
            </p>
          </div>

          <Badge variant={status.variant} className="flex items-center gap-2 text-base px-4 py-2">
            <StatusIcon className={`h-5 w-5 ${status.color}`} />
            {status.label}
          </Badge>
        </div>
      </div>

      {/* Alert de Governança */}
      {campaign.status === "CLOSED" && (
        <Alert className="bg-green-50 border-green-200">
          <Lock className="h-4 w-4 text-green-600" />
          <AlertDescription>
            <strong>Campanha Encerrada:</strong> Todos os dados são oficiais e imutáveis. Nenhuma alteração é permitida para garantir rastreabilidade e auditoria.
          </AlertDescription>
        </Alert>
      )}

      {/* Seção 1: Metadados da Campanha */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Metadados da Campanha
          </CardTitle>
          <CardDescription>Informações completas e auditáveis da campanha</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">Empresa</div>
              <div className="text-lg font-semibold">{campaign.companyName}</div>
              {campaign.companyCnpj && (
                <div className="text-sm text-muted-foreground">CNPJ: {campaign.companyCnpj}</div>
              )}
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">Template</div>
              <div className="text-lg font-semibold">
                {campaign.templateId} <Badge variant="secondary">v{campaign.templateVersion}</Badge>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">kMin (Anonimato)</div>
              <div className="text-lg font-semibold">{campaign.kMin}</div>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">Modo de Consolidação</div>
              <div className="text-lg font-semibold">
                <Badge>{campaign.consolidationMode}</Badge>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">Total de Respostas</div>
              <div className="text-lg font-semibold">
                <Badge variant={campaign.totalResponses >= campaign.kMin ? "default" : "secondary"}>
                  {campaign.totalResponses}
                </Badge>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">Snapshot Oficial</div>
              <div className="text-lg font-semibold">
                {campaign.hasSnapshot ? (
                  <Badge variant="default" className="bg-green-600">
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    Disponível
                  </Badge>
                ) : (
                  <Badge variant="secondary">Não disponível</Badge>
                )}
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <User className="h-4 w-4" />
              Criado por
            </div>
            <div className="text-lg font-semibold">{campaign.createdByUserName}</div>
            {campaign.createdByUserEmail && (
              <div className="text-sm text-muted-foreground">{campaign.createdByUserEmail}</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Seção 2: Linha do Tempo (Auditável) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Linha do Tempo (Auditável)
          </CardTitle>
          <CardDescription>Histórico completo de eventos da campanha</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {campaign.timeline.map((event, index) => {
              const isLast = index === campaign.timeline.length - 1;
              const eventConfig = {
                CREATED: { label: "Campanha Criada", icon: FileText, color: "text-blue-600" },
                ACTIVATED: { label: "Campanha Ativada", icon: CheckCircle2, color: "text-green-600" },
                CLOSED: { label: "Campanha Encerrada", icon: Lock, color: "text-red-600" },
              };

              const config = eventConfig[event.event as keyof typeof eventConfig];
              const EventIcon = config.icon;

              return (
                <div key={index} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`rounded-full p-2 bg-background border-2 ${config.color}`}>
                      <EventIcon className="h-5 w-5" />
                    </div>
                    {!isLast && <div className="w-0.5 h-full bg-border mt-2" />}
                  </div>

                  <div className="flex-1 pb-8">
                    <div className="font-semibold">{config.label}</div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(event.timestamp).toLocaleString("pt-BR")}
                    </div>
                    {event.user && (
                      <div className="text-sm text-muted-foreground">Por: {event.user}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Seção 3: Resultados Oficiais */}
      {campaign.status === "CLOSED" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Resultados Oficiais (VERDADE OFICIAL)
            </CardTitle>
            <CardDescription>
              Snapshot final imutável - Base oficial para Motor de Análise, Inventário de Riscos e Plano de Ação NR-1
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingSnapshot ? (
              <div className="text-center py-4 text-muted-foreground">Carregando snapshot final...</div>
            ) : finalSnapshot?.ok && finalSnapshot.snapshots.length > 0 ? (
              <div className="space-y-4">
                <Alert className="bg-green-50 border-green-200">
                  <Shield className="h-4 w-4 text-green-600" />
                  <AlertDescription>
                    <strong>OFICIAL:</strong> Estes são os resultados finais imutáveis. Nenhuma alteração é possível após o encerramento.
                  </AlertDescription>
                </Alert>

                {finalSnapshot.snapshots.map((snapshot) => (
                  <div key={snapshot.id} className="space-y-4 p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium">Escopo: {snapshot.scope}</div>
                        <div className="text-xs text-muted-foreground">
                          Consolidado em: {new Date(snapshot.createdAt).toLocaleString("pt-BR")}
                        </div>
                      </div>
                      <Badge variant="default" className="bg-green-600">
                        OFICIAL
                      </Badge>
                    </div>

                    <div className="space-y-2">
                      <div className="text-sm font-medium">
                        Score Geral: {snapshot.consolidatedData.overallScore.toFixed(2)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Qualificador ERGOS: <Badge>{snapshot.consolidatedData.qualifierLabel}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Total de Respostas: {snapshot.consolidatedData.totalResponses}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="text-sm font-medium">Scores por Módulo:</div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {Object.entries(snapshot.consolidatedData.moduleScores).map(([moduleCode, score]) => (
                          <div key={moduleCode} className="p-2 border rounded text-sm">
                            <div className="font-medium">{moduleCode}</div>
                            <div className="text-muted-foreground">{(score as number).toFixed(2)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Alert variant="destructive">
                <AlertDescription>
                  {finalSnapshot?.message || "Snapshot final não encontrado. A consolidação pode não ter sido executada."}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
